import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import CTAButton from '../../../../CTAButton';
import OcLabel from '../../../../ComponentLibrary/input-elements/oc-label';
import { connectLocation } from '../../../../location/LocationConnectors';
import { useTRC } from '../../store';
import { QuestionWrapper, SubQuestionWrapper } from '../../styles';
import generateResultsPdf from '../WallsAboveGrade/pdfResults';
import CalculatorLayout from '../shared/CalculatorLayout';
import Footer from '../shared/Footer';
import ResultsBlock from '../shared/ResultsBlock';
import {
  WOOD_STRAP_BEHIND_GWB_RSI,
  INTERIOR_GWB_RSI_PER_MM,
  AIR_FILM_INTERIOR_RSI,
  validateInputs,
  CODEBORD_THICKNESS_OPTIONS,
  RAINBARRIER_THICKNESS_OPTIONS,
  onCenterSpacingOptions,
  CODEBORD_RSI_PER_INCH,
  RAINBARRIER_RSI_PER_INCH,
  FramingText,
  resetResults,
  insulationOptions,
} from '../shared/constants';
import { DropdownField, Section, YesNo } from '../shared/formControls';
import { smoothScrollIntoView } from '../shared/scroll';

const rsiRange = {
  '2x4': { values: [12, 14] },
  '2x6': { values: [12, 14, 19, 20, 22, 24] },
};

export const toRSI = {
  12: 2.11,
  14: 2.46,
  19: 3.34,
  20: 3.52,
  22: 3.87,
  24: 4.23,
};

const rsiOptions = insulationOptions(rsiRange, toRSI);

const CONCRETE_RSI_BY_MM = {
  203: 0.08,
  254: 0.1,
  305: 0.12,
};

const FRAME_RSIF = {
  '2x4': 0.757,
  '2x6': 1.19,
};

// Fractions of area at the frame for each spacing (from R1-1 “Frame Cavity Percentage”)
const AF_BY_SPACING = { 304: 0.175, 406: 0.16, 488: 0.145, 610: 0.13 };

// —— defaults for this calc’s inputs
const DEFAULTS = {
  framingType: '2x4',
  onCenterSpacing: '304',
  cavityRSI: '',
  concreteFoundationThickness: '203',
  hasExteriorInsulation: true,
  exteriorInsulationType: '',
  codebordThickness: '',
  rainbarrierThickness: '',
  hasVapourBarrier: true,
  gypsumBoardThickness: '12.7',
  hasWoodStrapping: true,
};

const WallsBelowGrade = ({ calcId, t }) => {
  // ---- read location from the store
  const { state, setResults: publishResults, setInputs: publishInputs } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);

  // —— hydrate local state from store (if present), else use defaults
  const savedInputs = state.inputsByCalc?.[calcId];
  const [framingType, setFramingType] = useState(savedInputs?.framingType ?? DEFAULTS.framingType);
  const [onCenterSpacing, setOnCenterSpacing] = useState(savedInputs?.onCenterSpacing ?? DEFAULTS.onCenterSpacing);
  const [cavityRSI, setCavityRSI] = useState(savedInputs?.cavityRSI ?? DEFAULTS.cavityRSI);
  const [hasVapourBarrier, setHasVapourBarrier] = useState(savedInputs?.hasVapourBarrier ?? DEFAULTS.hasVapourBarrier);

  const [concreteFoundationThickness, setConcreteFoundationThickness] = useState(
    savedInputs?.concreteFoundationThickness ?? DEFAULTS.concreteFoundationThickness
  );

  const [hasExteriorInsulation, setHasExteriorInsulation] = useState(
    savedInputs?.hasExteriorInsulation ?? DEFAULTS.hasExteriorInsulation
  );

  const [exteriorInsulationType, setExteriorInsulationType] = useState(
    savedInputs?.exteriorInsulationType ?? DEFAULTS.exteriorInsulationType
  );

  const [codebordThickness, setCodebordThickness] = useState(
    savedInputs?.codebordThickness ?? DEFAULTS.codebordThickness
  );

  const [rainbarrierThickness, setRainbarrierThickness] = useState(
    savedInputs?.rainbarrierThickness ?? DEFAULTS.rainbarrierThickness
  );

  const [gypsumBoardThickness, setGypsumBoardThickness] = useState(
    savedInputs?.gypsumBoardThickness ?? DEFAULTS.gypsumBoardThickness
  );

  const [hasWoodStrapping, setHasWoodStrapping] = useState(savedInputs?.hasWoodStrapping ?? DEFAULTS.hasWoodStrapping);

  const resultsRef = useRef(null);
  const [error, setError] = useState(null);

  const hasFrame = framingType !== 'none';

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(null), 5000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [error]);

  // —— whenever inputs change, publish them to the store so tab switches persist
  useEffect(() => {
    publishInputs({
      calcId,
      inputs: {
        framingType,
        onCenterSpacing,
        cavityRSI,
        concreteFoundationThickness,
        hasExteriorInsulation,
        exteriorInsulationType,
        codebordThickness,
        rainbarrierThickness,
        hasVapourBarrier,
        gypsumBoardThickness,
        hasWoodStrapping,
      },
    });
  }, [
    calcId,
    framingType,
    onCenterSpacing,
    cavityRSI,
    concreteFoundationThickness,
    hasExteriorInsulation,
    exteriorInsulationType,
    codebordThickness,
    rainbarrierThickness,
    gypsumBoardThickness,
    hasWoodStrapping,
    hasVapourBarrier,
    publishInputs,
  ]);

  const results = state.resultsByCalc?.[calcId]?.results || null;

  const rowsForTable = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.center_of_cavity'), rsi: 'centerOfCavityRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI' },
    { label: t('thermal_calculator.exterior_insulation_rsi'), rsi: 'exteriorInsulationRSI', roundR: true },
  ];

  // ---- calculator
  const calculate = () => {
    const inches = v => parseFloat(String(v || '0'));
    const exteriorRSIFromSelection = () => {
      if (exteriorInsulationType === 'codebord') return CODEBORD_RSI_PER_INCH * inches(codebordThickness || '0');
      if (exteriorInsulationType === 'rainbarrier')
        return RAINBARRIER_RSI_PER_INCH * inches(rainbarrierThickness || '0');
      return 0;
    };

    const spacingText = onCenterSpacingOptions[onCenterSpacing];
    const nominalRsiText = hasFrame
      ? rsiOptions[framingType].find(opt => String(opt.value) === String(cavityRSI))?.text || ''
      : '';

    const gwbT = parseFloat(gypsumBoardThickness || '0');
    const RSIc = parseFloat(cavityRSI || '0');
    const concreteRSI = CONCRETE_RSI_BY_MM[concreteFoundationThickness] || 0;

    const interiorGwbRSI = INTERIOR_GWB_RSI_PER_MM * gwbT;
    const strapBehindGwbRSI = hasWoodStrapping ? WOOD_STRAP_BEHIND_GWB_RSI : 0;
    const vapourBarrierRSI = hasVapourBarrier ? 0 : 0; // explicit 0 (kept in ledger, colored differently in PDF)
    const exteriorInsulationRSI = hasExteriorInsulation ? exteriorRSIFromSelection() : 0;

    // ------- Parallel (only if there IS a framed finish wall) -------
    let RSIparallel = 0;
    let Af = 0;
    let Ac = 1;
    if (hasFrame) {
      Af = AF_BY_SPACING[onCenterSpacing] ?? 0.16;
      Ac = Math.max(0, 1 - Af);
      const RSIf = FRAME_RSIF[framingType] ?? 0;

      // Standard NBCC-style parallel sum (frame/cavity) for the core
      RSIparallel = RSIf > 0 && RSIc > 0 ? 1 / (Af / RSIf + Ac / RSIc) : RSIc || 0;
    }

    // ------- Continuous sum (foundation stack) -------
    const continuousTotalRSI =
      concreteRSI + exteriorInsulationRSI + interiorGwbRSI + strapBehindGwbRSI + AIR_FILM_INTERIOR_RSI;

    // ------- Outputs -------
    const effectiveAssemblyRSI = RSIparallel + continuousTotalRSI;
    const centerOfCavityRSI = RSIc + continuousTotalRSI;
    const installedInsulationRSI = (RSIc || 0) + (exteriorInsulationRSI || 0);

    // ------- PDF/Results ledger -------
    const layers = {
      continuous: [
        {
          id: 'concrete',
          label: `${t('thermal_calculator.walls_below_grade.concrete_wall')}: ${concreteFoundationThickness} mm`,
          rsi: concreteRSI,
        },
        ...(hasExteriorInsulation
          ? [
              {
                id: 'exterior_insulation',
                label:
                  exteriorInsulationType === 'codebord'
                    ? `${t('thermal_calculator.walls_above_grade.exterior_insulation_codebord')}: ${codebordThickness}"`
                    : `${t(
                        'thermal_calculator.walls_above_grade.exterior_insulation_rainbarrier'
                      )}: ${rainbarrierThickness}"`,
                rsi: exteriorInsulationRSI,
              },
            ]
          : []),
        { id: 'vb', label: t('thermal_calculator.roofs_below_attics.vapour_barrier'), rsi: vapourBarrierRSI },
        ...(hasWoodStrapping
          ? [
              {
                id: 'strap',
                label: t('thermal_calculator.walls_above_grade.steel_strapping_air_cavity'),
                rsi: strapBehindGwbRSI,
              },
            ]
          : []),
        ...(gwbT
          ? [
              {
                id: 'gwb',
                label: `${t('thermal_calculator.floors_over_unheated_spaces.gypsum_board')}: ${gwbT} mm`,
                rsi: interiorGwbRSI,
              },
            ]
          : []),
        {
          id: 'film_int',
          label: t('thermal_calculator.floors_over_unheated_spaces.interior_air_film'),
          rsi: AIR_FILM_INTERIOR_RSI,
        },
      ],
      nonContinuous: hasFrame
        ? [
            {
              id: 'finish_wall_core',
              label: [
                // eslint-disable-next-line max-len
                `${t('thermal_calculator.floors_over_unheated_spaces.wood_studs')} (${
                  FramingText[framingType]
                }), ${spacingText} ${t(
                  'thermal_calculator.floors_over_unheated_spaces.on_center'
                )}, ${nominalRsiText} ${t('thermal_calculator.floors_over_unheated_spaces.nominal_insulation')}`,
              ],
              rsi: RSIparallel,
            },
          ]
        : [],
    };

    return {
      Af,
      Ac,
      RSIf: hasFrame ? FRAME_RSIF[framingType] ?? 0 : 0,
      RSIc,
      RSIparallel,
      interiorGwbRSI,
      strapBehindGwbRSI,
      concreteRSI,
      exteriorInsulationRSI,
      continuousTotalRSI,
      effectiveAssemblyRSI,
      centerOfCavityRSI,
      installedInsulationRSI,
      layers,
    };
  };

  const onCalculate = () => {
    if (!validateInputs({ province, region, setError, cavityRSI, validateCavityRSI: false })) {
      smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
      // do not change persisted inputs; just report error
      return;
    }
    // inputs are valid, clear any previous error
    setError(null);

    // perform calculation
    const res = calculate();
    publishResults({
      calcId,
      results: res,
      meta: { title: t('thermal_calculator.titles.walls_below_grade') },
    });
    smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
  };

  const onReset = () => {
    resetResults(calcId, t('thermal_calculator.titles.walls_below_grade'), publishResults);
    // reset local inputs to defaults
    setFramingType(DEFAULTS.framingType);
    setOnCenterSpacing(DEFAULTS.onCenterSpacing);
    setCavityRSI(DEFAULTS.cavityRSI);

    setConcreteFoundationThickness(DEFAULTS.concreteFoundationThickness);
    setHasVapourBarrier(DEFAULTS.hasVapourBarrier);
    setGypsumBoardThickness(DEFAULTS.gypsumBoardThickness);
    setHasWoodStrapping(DEFAULTS.hasWoodStrapping);
    setError(null);
    // clear inputs in store
    publishInputs({ calcId, inputs: null });
  };

  const handleFramingTypeChange = value => {
    setFramingType(value);
    setCavityRSI('');
    if (value === 'none') {
      setOnCenterSpacing('');
    }
  };

  // ---- sidebar UI
  const sidebar = (
    <>
      <Section title={t('thermal_calculator.non_continuous_layers')}>
        <QuestionWrapper>
          <OcLabel forId="on-center-spacing" text={t('thermal_calculator.framing_type')} />
          <DropdownField
            id={`framing-type-${calcId}`}
            value={framingType}
            onChange={e => {
              handleFramingTypeChange(e.target.value);
            }}
            options={[
              { value: '2x4', text: `${t('thermal_calculator.walls_above_grade.wood_studs')} 2x4` },
              { value: '2x6', text: `${t('thermal_calculator.walls_above_grade.wood_studs')} 2x6` },
              { value: 'none', text: t('thermal_calculator.none') },
            ]}
          />
        </QuestionWrapper>

        {hasFrame && (
          <>
            <QuestionWrapper>
              <OcLabel
                forId="on-center-spacing"
                text={t('thermal_calculator.floors_over_unheated_spaces.framing_on_center')}
              />
              <DropdownField
                id={`on-center-spacing-${calcId}`}
                value={onCenterSpacing}
                onChange={e => {
                  setOnCenterSpacing(e.target.value);
                }}
                options={Object.entries(onCenterSpacingOptions).map(([value, text]) => ({
                  value,
                  text,
                }))}
              />
            </QuestionWrapper>

            <QuestionWrapper>
              <OcLabel
                forId={`cavityRSI-${calcId}`}
                text={t('thermal_calculator.floors_over_unheated_spaces.cavity_nominal_rsi')}
              />
              <DropdownField
                id={`cavityRSI-${calcId}`}
                value={cavityRSI}
                onChange={e => {
                  setCavityRSI(e.target.value);
                }}
                options={hasFrame ? rsiOptions[framingType] || [] : []}
              />
            </QuestionWrapper>
          </>
        )}
      </Section>

      <Section title={t('thermal_calculator.continuous_layers')}>
        <QuestionWrapper>
          <OcLabel
            forId={`concrete-thickness-${calcId}`}
            text={t('thermal_calculator.walls_below_grade.foundation_wall_thickness')}
          />
          <DropdownField
            id={`concrete-thickness-${calcId}`}
            value={concreteFoundationThickness}
            onChange={e => {
              setConcreteFoundationThickness(e.target.value);
            }}
            options={[
              { value: '203', text: '203 mm (8")' },
              { value: '254', text: '254 mm (10")' },
              { value: '305', text: '305 mm (12")' },
            ]}
          />
        </QuestionWrapper>

        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.walls_below_grade.have_exterior_insulation')} />
          <YesNo
            name={`exterior-insulation-${calcId}`}
            value={hasExteriorInsulation}
            onChange={val => {
              setHasExteriorInsulation(val);
            }}
          />
        </QuestionWrapper>

        {hasExteriorInsulation && (
          <div>
            <QuestionWrapper>
              <OcLabel text={t('thermal_calculator.walls_below_grade.select_insulation_type')} />
              <DropdownField
                id={`extType-${calcId}`}
                value={exteriorInsulationType}
                onChange={e => setExteriorInsulationType(e.target.value)}
                options={[
                  { value: 'codebord', text: 'CodeBord' },
                  { value: 'rainbarrier', text: 'Rainbarrier ci 80' },
                ]}
              />
            </QuestionWrapper>

            {exteriorInsulationType === 'codebord' ? (
              <SubQuestionWrapper>
                <OcLabel text={t('thermal_calculator.walls_below_grade.select_codebord_thickness')} />
                <DropdownField
                  id={`ext-codebord-${calcId}`}
                  value={codebordThickness}
                  onChange={e => setCodebordThickness(e.target.value)}
                  options={CODEBORD_THICKNESS_OPTIONS}
                />
              </SubQuestionWrapper>
            ) : (
              <SubQuestionWrapper>
                <OcLabel text={t('thermal_calculator.walls_below_grade.select_rainbarrier_thickness')} />
                <DropdownField
                  id={`ext-rain-barrier-${calcId}`}
                  value={rainbarrierThickness}
                  onChange={e => setRainbarrierThickness(e.target.value)}
                  options={RAINBARRIER_THICKNESS_OPTIONS}
                />
              </SubQuestionWrapper>
            )}
          </div>
        )}

        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.form.vapour_barrier_winter')} />
          <YesNo
            name={`vapour-barrier-${calcId}`}
            value={hasVapourBarrier}
            onChange={val => {
              setHasVapourBarrier(val);
            }}
          />
        </QuestionWrapper>

        <QuestionWrapper>
          <OcLabel
            forId={`gypsum-thickness-${calcId}`}
            text={t('thermal_calculator.walls_above_grade.gypsum_board_thickness_wall')}
          />
          <DropdownField
            id={`gypsum-thickness-${calcId}`}
            value={gypsumBoardThickness}
            onChange={e => {
              setGypsumBoardThickness(e.target.value);
            }}
            options={[
              { value: '12.7', text: '12.7 mm (1/2")' },
              { value: '16', text: '16 mm (5/8")' },
              { value: '0', text: t('thermal_calculator.none') },
            ]}
          />
        </QuestionWrapper>

        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.walls_below_grade.wood_strapping_behind')} />
          <YesNo
            name={`wood-strapping-${calcId}`}
            value={hasWoodStrapping}
            onChange={val => {
              setHasWoodStrapping(val);
            }}
          />
        </QuestionWrapper>

        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <CTAButton
            onClick={onCalculate}
            disabled={!hasLocation}
            text={t('thermal_calculator.calculate_r_value')}
            style={{ minWidth: '200px' }}
          />
          <CTAButton
            element="button"
            type="button"
            onClick={onReset}
            text={t('thermal_calculator.reset')}
            style={{
              minWidth: '100px',
              padding: '8px 16px',
              border: '1px solid #ccc',
              background: '#fff',
              color: '#000',
            }}
          />
        </div>
      </Section>
    </>
  );

  const handleDownload = () => {
    if (!results) return;
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      projectLink: 'https://insulation.owenscorning.ca/builders/calculators/thermal-project-calculator/',
      assemblyHeading: t('thermal_calculator.titles.walls_below_grade'),
      tableTitle: t('thermal_calculator.entire_assembly'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      rowsConfig: rowsForTable,
      results,
      t,
    });
  };

  const resultsPane = (
    <div ref={resultsRef}>
      <ResultsBlock
        calcId={calcId}
        province={province}
        region={region}
        hdd={hdd}
        error={error}
        results={results}
        rows={rowsForTable}
      />
      <Footer results={results} onDownload={handleDownload} />
    </div>
  );

  return <CalculatorLayout sidebar={sidebar} results={resultsPane} />;
};

WallsBelowGrade.propTypes = {
  calcId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connectLocation(WallsBelowGrade);
